/**
 * What a move does here, in one football line under its tile (slice 8d). Built from the move's
 * football effect and balance.json only: the sole fixed words are the stat names and what each stat
 * wins (matchup.ts STRENGTH in words), never anything per move.
 */
import {
  ALL_TYPES,
  balance as defaultBalance,
  effectiveness,
  type Balance,
  type MoveEffect,
  type StatName,
} from '@tdmons/sim';

type BattleStat = Exclude<StatName, 'hp'>;

const STAT_NAME: Record<BattleStat, string> = {
  atk: 'Attack',
  def: 'Defense',
  spa: 'Sp. Atk',
  spd: 'Sp. Def',
  spe: 'Speed',
};

/** The battles each stat wins (block = avg(Atk, Def) vs Atk, throw = SpA, catch = SpD, ...). */
const STAT_WINS: Record<BattleStat, string> = {
  atk: 'blocks, rushes and tackles',
  def: 'blocks and broken tackles',
  spa: 'throws and kicks',
  spd: 'contested catches',
  spe: 'coverage and separation',
};

const plays = (n: number) => `${n} play${n === 1 ? '' : 's'}`;
const pct = (x: number) => `${Math.round(x * 100)}%`;
const signed = (n: number) => `${n > 0 ? '+' : '-'}${Math.abs(n)}`;
/** 0.5 reads "half"; anything else as a multiplier. */
const share = (m: number) => (m === 0.5 ? 'half' : `${m}x`);

/** "Fire attack · fought in his first battle · strong vs Grass, Ice, Bug, Steel". */
export function moveLine(e: MoveEffect, b: Balance = defaultBalance): string {
  switch (e.kind) {
    case 'attack': {
      const strong = ALL_TYPES.filter((t) => effectiveness(e.type, [t]) > 1);
      return [
        `${e.type} attack`,
        'fought in his first battle',
        ...(strong.length ? [`strong vs ${strong.join(', ')}`] : []),
        ...(e.recoil ? ['recoil'] : []),
        ...(e.drain ? ['drains HP'] : []),
        ...(e.selfKo ? ['he drops to 0 HP'] : []),
      ].join(' · ');
    }
    case 'burst':
      return `${signed(e.speedStages)} Speed this snap · wins ${STAT_WINS.spe}`;
    case 'stage':
      return e.stages > 0
        ? `${signed(e.stages)} ${STAT_NAME[e.stat]} · wins ${STAT_WINS[e.stat]} for the drive`
        : `${signed(e.stages)} ${STAT_NAME[e.stat]} · for the drive`;
    case 'debuff':
      return `Their ${STAT_NAME[e.stat]} ${signed(e.stages)} · for the drive`;
    case 'status': {
      const s = b.status;
      switch (e.status) {
        case 'paralysis':
          return `Paralyzes · they move at ${share(s.paralysis.speedMult)} speed for ${plays(s.paralysis.plays)}`;
        case 'burn':
          return `Burns · their Attack is ${s.burn.atkMult === 0.5 ? 'halved' : `${s.burn.atkMult}x`} for ${plays(s.burn.plays)}`;
        case 'sleep':
          return s.sleep.plays === 1
            ? 'Puts to sleep · they miss the next snap'
            : `Puts to sleep · they miss the next ${s.sleep.plays} snaps`;
        case 'confusion':
          return `Confuses · ${pct(s.confusion.wrongRouteChance)} wrong route for ${plays(s.confusion.plays)}`;
      }
      break;
    }
    case 'heal':
      return [
        'Rest',
        'on the bench only',
        `heals ${pct(e.fraction)}`,
        ...(e.sitPlays > 0 ? [`sits ${plays(e.sitPlays)}`] : []),
      ].join(' · ');
    case 'protect':
      return 'Protect · holds one rusher';
    case 'none':
      return 'No football effect';
  }
  return 'No football effect';
}
